"use client";

import type { SoundEffect, SoundEffectCategory } from "@mw/shared/payload-types";

import { searchSoundEffects } from "@/app/(pages)/[locale]/(default)/(old-footer-in-page)/sound-effects/search";
import { captureEvent } from "@/app/_analytics/analytics";
import { EventName } from "@mw/shared/analyticsEventNames";
import { Link } from "@components/core/Link";
import { Popover, PopoverContent, PopoverTrigger } from "@components/core/Popover";
import { logger } from "@utilities/logger";
import { cn } from "@ui/lib/utils";
import { Howl } from "howler";
import { SearchIcon } from "lucide-react";
import { useTranslations } from "next-intl";
import { useEffect, useMemo, useRef, useState, useTransition } from "react";
import { useDebounceValue } from "usehooks-ts";

import { Button } from "../core/Button";

interface SearchSoundEffectsInputProps {
  className?: string;
}

interface FilteredSound {
  categoryName: string;
  categorySlug: string;
  description?: string;
  id: string;
  name: string;
  shortDescription?: string;
  soundUrl?: string;
}

type SearchState = "hasResults" | "loading" | "noResults" | "typeToSearch";

const SearchSoundEffectsInput = ({ className }: SearchSoundEffectsInputProps) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredSounds, setFilteredSounds] = useState<FilteredSound[]>([]);
  const [isPending, startTransition] = useTransition();
  const [isOpen, setIsOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [playingId, setPlayingId] = useState<null | string>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const howlRef = useRef<Howl | null>(null);
  const currentRequestIdRef = useRef<null | string>(null);
  const t = useTranslations("ClientKeys.search_sound_effects_input");
  const tGlobals = useTranslations("ClientKeys.Globals");
  // Debounce the search query with 1 second delay
  const [debouncedSearchQuery] = useDebounceValue(searchQuery, 300);

  // Derived state for cleaner rendering logic - only one state at a time
  const searchState: SearchState = useMemo(() => {
    const hasQuery = searchQuery.trim().length > 2;
    const hasResults = filteredSounds.length > 0;
    const isWaitingForDebounce = hasQuery && searchQuery !== debouncedSearchQuery;

    if (!hasQuery) {
      return "typeToSearch";
    }

    if (isPending || isWaitingForDebounce) {
      return "loading";
    }

    if (hasResults) {
      return "hasResults";
    }

    return "noResults";
  }, [searchQuery, debouncedSearchQuery, filteredSounds.length, isPending]);

  const filterSounds = (categories: SoundEffectCategory[], query: string): FilteredSound[] => {
    const lowerQuery = query.toLowerCase();
    const filtered: FilteredSound[] = [];

    categories.forEach(category => {
      if (category.sounds) {
        category.sounds.forEach((sound: SoundEffect | string) => {
          if (typeof sound === "string") return;

          const matchesName = sound.name?.toLowerCase().includes(lowerQuery);
          const matchesShortDesc = sound.shortDescription?.toLowerCase().includes(lowerQuery);
          const matchesDesc = sound.description?.toLowerCase().includes(lowerQuery);

          if (matchesName || matchesShortDesc || matchesDesc) {
            // Get the sound URL from either url field or media object
            const soundUrl = sound.url || (typeof sound.media === "string" ? sound.media : sound.media?.url);

            filtered.push({
              id: sound.id,
              name: sound.name,
              shortDescription: sound.shortDescription,
              description: sound.description || "",
              categoryName: category.title || category.name || "",
              categorySlug: category.slug || "",
              soundUrl: soundUrl || undefined,
            });
          }
        });
      }
    });

    return filtered;
  };

  // Effect to perform search when debounced query changes
  useEffect(() => {
    const performSearch = (query: string) => {
      if (query.trim().length > 2) {
        setIsOpen(true);

        // Generate unique request ID
        const requestId = `search-${Date.now()}-${Math.random()}`;
        currentRequestIdRef.current = requestId;

        startTransition(async () => {
          try {
            const results = await searchSoundEffects(query);

            // Only process results if this is still the current request
            if (currentRequestIdRef.current === requestId) {
              const filtered = filterSounds(results.docs || [], query);
              setFilteredSounds(filtered);
              captureEvent(EventName.marketing_website_sound_effects_search, {
                search_query: query,
                search_results_count: filtered.length,
              });
            }
            // If it's not the current request, silently ignore the results
          } catch (error) {
            // Only log error if this is still the current request
            if (currentRequestIdRef.current === requestId) {
              logger.error(error instanceof Error ? error : null, "Search error");
              setFilteredSounds([]);
            }
          }
        });
      } else {
        // Clear current request when query is too short
        currentRequestIdRef.current = null;
        setFilteredSounds([]);
        setIsOpen(false);
      }
    };

    performSearch(debouncedSearchQuery);
  }, [debouncedSearchQuery]);

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    setFocusedIndex(-1); // Reset focus when search changes
  };

  const handlePlayPause = (sound: FilteredSound) => {
    if (!sound.soundUrl) return;

    // If this sound is already playing, pause it
    if (playingId === sound.id && isPlaying) {
      howlRef.current?.pause();
      setIsPlaying(false);
      return;
    }

    // If a different sound is playing, stop it
    if (howlRef.current && playingId !== sound.id) {
      howlRef.current.stop();
    }

    // If this is a new sound or resuming the same sound
    if (playingId !== sound.id) {
      // Create new Howl instance
      howlRef.current = new Howl({
        src: [sound.soundUrl],
        onplay: () => {
          setIsPlaying(true);
          setPlayingId(sound.id);
        },
        onpause: () => {
          setIsPlaying(false);
        },
        onstop: () => {
          setIsPlaying(false);
          setPlayingId(null);
        },
        onend: () => {
          setIsPlaying(false);
          setPlayingId(null);
        },
      });
      howlRef.current.play();
    } else {
      // Resume the same sound
      howlRef.current?.play();
      setIsPlaying(true);
    }
  };

  // Cleanup Howl instance, abort controller on unmount
  useEffect(() => {
    return () => {
      if (howlRef.current) {
        howlRef.current.stop();
        howlRef.current = null;
      }
    };
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen || filteredSounds.length === 0) return;

    if (["ArrowDown", "ArrowUp", "Enter", "Escape", "Tab"].includes(e.key)) {
      e.preventDefault();
    }

    switch (e.key) {
      case "ArrowDown":
        setFocusedIndex(prev => {
          const newIndex = prev < filteredSounds.length - 1 ? prev + 1 : prev;
          // Scroll focused item into view
          setTimeout(() => {
            const element = document.getElementById(`search-option-${newIndex}`);
            element?.scrollIntoView({ block: "nearest" });
          }, 0);
          return newIndex;
        });
        break;
      case "ArrowUp":
        setFocusedIndex(prev => {
          const newIndex = prev > 0 ? prev - 1 : -1;
          // Scroll focused item into view
          setTimeout(() => {
            if (newIndex >= 0) {
              const element = document.getElementById(`search-option-${newIndex}`);
              element?.scrollIntoView({ block: "nearest" });
            }
          }, 0);
          return newIndex;
        });
        break;
      case "Enter":
        if (focusedIndex >= 0 && focusedIndex < filteredSounds.length) {
          // Focus the first button in the selected option so users can tab between buttons
          const firstButton = document.querySelector(`#search-option-${focusedIndex} button`) as HTMLButtonElement;
          if (firstButton) {
            firstButton.focus();
          }
        }
        break;
      case "Escape":
        setIsOpen(false);
        setFocusedIndex(-1);
        break;
      case "Tab":
        if (filteredSounds.length > 0) {
          e.preventDefault();
          setFocusedIndex(prev => {
            const newIndex = prev < filteredSounds.length - 1 ? prev + 1 : 0;
            // Scroll focused item into view
            setTimeout(() => {
              const element = document.getElementById(`search-option-${newIndex}`);
              element?.scrollIntoView({ block: "nearest" });
            }, 0);
            return newIndex;
          });
        }
        break;
    }
  };

  return (
    <div className={cn("tw-relative", className)}>
      <Popover onOpenChange={setIsOpen} open={isOpen}>
        <PopoverTrigger asChild>
          <div className='tw-relative'>
            <input
              aria-activedescendant={focusedIndex >= 0 ? `search-option-${focusedIndex}` : undefined}
              aria-autocomplete='list'
              aria-controls='search-results-listbox'
              aria-expanded={isOpen}
              aria-haspopup='listbox'
              className='tw-h-10 tw-w-full tw-rounded-full tw-border tw-border-gray-400 tw-px-4 tw-pl-8 tw-pr-10 focus:tw-outline-none'
              onChange={e => handleSearch(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={t("placeholder")}
              role='combobox'
              type='text'
              value={searchQuery}
            />
            <div className='tw-pointer-events-none tw-absolute tw-left-3 tw-top-1/2 -tw-translate-y-1/2'>
              <SearchIcon size={14} />
            </div>
          </div>
        </PopoverTrigger>

        <PopoverContent
          align='start'
          className='tw-min-h-[50px] tw-max-h-[200px] tw-w-[var(--radix-popover-trigger-width)] tw-overflow-y-auto tw-rounded-xl tw-border tw-bg-paper tw-p-0 tw-shadow-xs'
          onCloseAutoFocus={e => e.preventDefault()}
          onOpenAutoFocus={e => e.preventDefault()}
        >
          {searchState === "loading" && (
            <div className='tw-p-4 tw-text-center tw-type-sm tw-text-gray-400'>{tGlobals("loading")}</div>
          )}

          {searchState === "hasResults" && (
            <div id='search-results-listbox' role='listbox'>
              {filteredSounds.map((sound, index) => (
                <div
                  aria-selected={index === focusedIndex}
                  className={cn(
                    "tw-flex tw-w-full tw-justify-between tw-border-b tw-p-4 tw-text-left tw-no-underline tw-transition-colors tw-duration-150 last:tw-border-b-0 hover:tw-bg-gray-100",
                    index === focusedIndex && "tw-bg-gray-100",
                  )}
                  id={`search-option-${index}`}
                  key={sound.id}
                  role='option'
                >
                  <div className='tw-mr-4 tw-min-w-0 tw-flex-1'>
                    <div className='tw-type-sm tw-truncate tw-normal-case'>{sound.shortDescription || sound.name}</div>
                    <div className='tw-mt-1 tw-truncate tw-type-xs tw-text-gray-400'>{sound.categoryName}</div>
                  </div>
                  <div className='tw-flex tw-shrink-0 tw-gap-2'>
                    <Button
                      disabled={!sound.soundUrl}
                      onClick={e => {
                        e.stopPropagation();
                        handlePlayPause(sound);
                      }}
                      size='sm'
                      variant='primary'
                    >
                      {playingId === sound.id && isPlaying ? "Pause" : "Play"}
                    </Button>
                    <Link
                      asButton
                      buttonVariants={{ variant: "secondary", size: "sm" }}
                      href={`/sound-effects/${sound.categorySlug}#${sound.id}`}
                    >
                      {t("view_more")}
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}

          {searchState === "noResults" && (
            <div className='tw-p-4 tw-text-center tw-type-sm tw-text-gray-400'>
              {t.rich("no_results_generate", {
                link: chunks => (
                  <Link
                    className='tw-text-black hover:tw-underline'
                    href='/sound-effects'
                    onClick={() => {
                      sessionStorage.setItem("soundEffectPrompt", searchQuery.trim());
                    }}
                  >
                    {chunks}
                  </Link>
                ),
              })}
            </div>
          )}

          {searchState === "typeToSearch" && (
            <div className='tw-p-4 tw-text-center tw-type-sm tw-text-gray-400'>{t("type_to_search")}</div>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
};

export { SearchSoundEffectsInput };
